import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowUpRight, Mail, Loader2, Users, Sparkles, Landmark, Copy, CheckCircle2 } from "lucide-react";

const DURATIONS = [
    { key: "1m", label: "1 Mês" },
    { key: "6m", label: "6 Meses" },
    { key: "12m", label: "12 Meses" },
];

export default function SubscriptionPage() {
    const { user, refresh } = useAuth();
    const [plans, setPlans] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [data, setData] = useState(null);
    const [duration, setDuration] = useState("1m");
    const [busy, setBusy] = useState(false);
    const [order, setOrder] = useState(null); // { reference, amount, currency, plan_name, bank }
    const [copied, setCopied] = useState("");
    const location = useLocation();

    const load = useCallback(async () => {
        try {
            const [p, s] = await Promise.all([api.get("/subscriptions/plans"), api.get("/subscriptions/my")]);
            setPlans(p.data.plans);
            setTiers(p.data.tiers);
            setData(s.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);

    useEffect(() => { load(); }, [location.key, load]);

    const canBuy = user?.role === "company_manager";
    const planFor = (tierId, dur) => plans.find((p) => p.tier === tierId && p.id.endsWith(`_${dur}`));

    const buy = async (planId) => {
        if (!canBuy) { toast.error("Apenas o Gestor de Empresa pode encomendar planos."); return; }
        setBusy(true);
        try {
            const { data: res } = await api.post("/subscriptions/request-manual", { plan_id: planId });
            setOrder(res);
            await refresh();
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(""), 1500);
        } catch (e) {
            toast.error("Não foi possível copiar");
        }
    };

    const closeOrder = () => setOrder(null);

    const current = data?.subscription;
    const history = data?.history || [];

    return (
        <div className="space-y-10" data-testid="subscription-page">
            <div>
                <div className="label-caps">Subscrição</div>
                <h1 className="font-display text-5xl text-[#0d1628] mt-4">Plano da Empresa</h1>
                <p className="text-[#52525b] mt-3 max-w-2xl">Encomende ou renove o plano da sua empresa. O pagamento é feito por transferência bancária para o IBAN da Solfut — a ativação é processada manualmente assim que confirmarmos a receção.</p>
            </div>

            {current && (
                <div className={`card-flat ${current.warning ? "border-[#b89b6f]" : ""}`} data-testid="current-subscription-card">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="label-caps">Estado atual</div>
                            <div className="flex items-baseline gap-4 mt-3 flex-wrap">
                                <span className="font-display text-3xl text-[#0d1628]">
                                    {current.status === "active" ? "Ativa" :
                                     current.status === "pending" ? "Aguarda transferência" :
                                     current.status === "paid_pending_invoice" ? "Pagamento confirmado · fatura pendente" :
                                     current.status === "expired" ? "Expirada" : "Sem subscrição"}
                                </span>
                                {current.plan_name && <span className="label-caps">· {current.plan_name}</span>}
                            </div>
                            {current.payment_reference && current.status !== "active" && (
                                <div className="text-[#52525b] mt-3 text-sm" data-testid="sub-payment-reference">
                                    Referência da transferência: <strong className="text-[#0d1628] font-mono">{current.payment_reference}</strong>
                                </div>
                            )}
                            {current.ends_at && (
                                <div className="text-[#52525b] mt-3 text-sm" data-testid="sub-ends-at">
                                    Termina a <strong className="text-[#0d1628]">{current.ends_at.slice(0, 10)}</strong>
                                    {current.days_remaining !== undefined && ` · ${current.days_remaining} dia(s) restantes`}
                                </div>
                            )}
                            {user?.users_limit && (
                                <div className="text-[#52525b] mt-2 text-sm flex items-center gap-2">
                                    <Users size={14} className="text-[#b89b6f]" />
                                    <span>Utilizadores: <strong className="text-[#0d1628]">{user.users_count}</strong> / {user.users_limit}</span>
                                </div>
                            )}
                        </div>
                        {current.warning && (
                            <div className="bg-[#b89b6f]/15 border border-[#b89b6f] text-[#0d1628] px-5 py-3 text-sm" data-testid="warning-banner">
                                A subscrição expira em breve — adquira um novo plano para continuar.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {canBuy && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="duration-toggle">
                    <span className="label-caps mr-2">Período:</span>
                    {DURATIONS.map((d) => (
                        <button key={d.key} onClick={() => setDuration(d.key)} className={`px-5 py-2 text-sm border ${duration === d.key ? "bg-[#0d1628] text-white border-[#0d1628]" : "bg-white text-[#0d1628] border-[#e4e4e7]"}`} data-testid={`dur-${d.key}`}>
                            {d.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tiers-grid">
                {tiers.map((t) => {
                    const plan = !t.contact_only ? planFor(t.id, duration) : null;
                    const isFeatured = t.id === "business";
                    return (
                        <article key={t.id} className={`card-flat flex flex-col relative ${isFeatured ? "border-[#0d1628]" : ""}`} data-testid={`sub-tier-${t.id}`}>
                            {isFeatured && (
                                <div className="absolute -top-3 left-6 bg-[#0d1628] text-white text-xs px-3 py-1 tracking-wider uppercase flex items-center gap-2">
                                    <Sparkles size={10} /> Recomendado
                                </div>
                            )}
                            <div className="label-caps text-[#b89b6f]">{t.for_companies}</div>
                            <h3 className="font-display text-3xl text-[#0d1628] mt-3">{t.name}</h3>
                            <div className="flex items-center gap-2 mt-3 text-xs text-[#52525b]">
                                <Users size={12} className="text-[#b89b6f]" />
                                {t.max_users ? <span>até {t.max_users} utilizadores</span> : <span>Sem limite</span>}
                            </div>
                            {t.contact_only ? (
                                <div className="font-display text-2xl text-[#0d1628] mt-5">Sob consulta</div>
                            ) : plan ? (
                                <div className="flex items-baseline gap-2 mt-5">
                                    <span className="font-display text-4xl text-[#0d1628]">€{plan.amount.toFixed(0)}</span>
                                </div>
                            ) : null}
                            <ul className="mt-6 space-y-2 text-sm text-[#0d1628] flex-1">
                                <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-[#b89b6f]" /> Acesso completo</li>
                                <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-[#b89b6f]" /> Projetos ilimitados</li>
                                <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-[#b89b6f]" /> Metodologia IHTP®</li>
                                {(t.id === "enterprise" || t.id === "corporate") && (
                                    <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-[#b89b6f]" /> Onboarding dedicado</li>
                                )}
                                {t.id === "corporate" && (
                                    <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-[#b89b6f]" /> SSO + Account Manager</li>
                                )}
                            </ul>

                            {!canBuy ? (
                                <div className="text-xs text-[#52525b] mt-6">Apenas o Gestor de Empresa pode adquirir planos.</div>
                            ) : t.contact_only ? (
                                <a href="https://ihavethepower.net/contactos/" target="_blank" rel="noreferrer" className="btn-outline mt-6" data-testid={`buy-${t.id}`}>
                                    <Mail size={14} /> Contactar IHTP
                                </a>
                            ) : plan ? (
                                <button onClick={() => buy(plan.id)} disabled={busy} className={`mt-6 ${isFeatured ? "btn-primary" : "btn-outline"}`} data-testid={`buy-${t.id}`}>
                                    {busy ? <Loader2 size={14} className="animate-spin" /> : <><Landmark size={14} /> Encomendar</>}
                                </button>
                            ) : null}
                        </article>
                    );
                })}
            </div>

            {history.length > 0 && (
                <div>
                    <div className="label-caps">Histórico</div>
                    <div className="card-flat mt-4 p-0 overflow-hidden" data-testid="history-table">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#e4e4e7]">
                                    <th className="text-left p-4 label-caps">Plano</th>
                                    <th className="text-left p-4 label-caps">Estado</th>
                                    <th className="text-left p-4 label-caps">Início</th>
                                    <th className="text-left p-4 label-caps">Fim</th>
                                    <th className="text-left p-4 label-caps">Método</th>
                                    <th className="text-left p-4 label-caps">Fatura</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((s) => (
                                    <tr key={s.id} className="border-b border-[#e4e4e7] last:border-0">
                                        <td className="p-4 text-[#0d1628]">{s.plan}</td>
                                        <td className="p-4 capitalize">{s.status}</td>
                                        <td className="p-4 text-[#52525b]">{s.starts_at ? s.starts_at.slice(0, 10) : "—"}</td>
                                        <td className="p-4 text-[#52525b]">{s.ends_at ? s.ends_at.slice(0, 10) : "—"}</td>
                                        <td className="p-4 text-[#52525b] capitalize">{s.payment_method || "—"}</td>
                                        <td className="p-4 text-[#52525b]">
                                            <div className="flex flex-col gap-1 text-xs">
                                                {s.moloni_invoice_pdf || s.moloni_pdf_url ? (
                                                    <a href={s.moloni_invoice_pdf || s.moloni_pdf_url} target="_blank" rel="noreferrer" className="link-underline" data-testid={`invoice-link-${s.id}`}>
                                                        Ver fatura
                                                    </a>
                                                ) : s.moloni_invoice_id || s.moloni_document_id ? (
                                                    <span>Fatura #{s.moloni_invoice_id || s.moloni_document_id}</span>
                                                ) : null}
                                                {s.moloni_receipt_pdf ? (
                                                    <a href={s.moloni_receipt_pdf} target="_blank" rel="noreferrer" className="link-underline" data-testid={`receipt-link-${s.id}`}>
                                                        Ver recibo
                                                    </a>
                                                ) : s.moloni_receipt_id ? (
                                                    <span>Recibo #{s.moloni_receipt_id}</span>
                                                ) : null}
                                                {!(s.moloni_invoice_id || s.moloni_document_id) && <span>—</span>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Order confirmation modal with bank-transfer details */}
            {order && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d1628]/70 backdrop-blur-sm p-4" data-testid="order-modal" onClick={closeOrder}>
                    <div className="bg-white border border-[#e4e4e7] w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="px-8 pt-8 pb-6 border-b border-[#e4e4e7]">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 size={28} className="text-[#b89b6f]" />
                                <h2 className="font-display text-3xl text-[#0d1628]" data-testid="order-modal-title">Encomenda registada</h2>
                            </div>
                            <p className="text-[#52525b] mt-4 text-sm leading-relaxed">
                                Para concluir a ativação do plano <strong className="text-[#0d1628]">{order.plan_name}</strong>, efetue uma transferência bancária para o IBAN indicado, <strong>incluindo a referência</strong> no descritivo. Receberá um email com estes dados.
                            </p>
                        </div>
                        <div className="px-8 py-6 space-y-3">
                            <BankRow label="Beneficiário" value={order.bank.beneficiary} testid="bank-beneficiary" />
                            <BankRow label="Banco" value={order.bank.bank_name} testid="bank-name" />
                            <BankRow label="IBAN" value={order.bank.iban} testid="bank-iban" mono onCopy={() => copyToClipboard(order.bank.iban.replace(/\s/g, ""), "iban")} copied={copied === "iban"} />
                            <BankRow label="Montante" value={`€${order.amount.toFixed(2)}`} testid="bank-amount" highlight />
                            <BankRow label="Referência" value={order.reference} testid="bank-reference" mono onCopy={() => copyToClipboard(order.reference, "reference")} copied={copied === "reference"} />
                        </div>
                        <div className="px-8 pb-8 pt-4 border-t border-[#e4e4e7] bg-[#f9f9fa]">
                            <p className="text-xs text-[#52525b] leading-relaxed">
                                A ativação pode demorar até 1 dia útil após a receção da transferência. Em caso de dúvida, contacte <a href="mailto:happiness@solfut.com" className="link-underline">happiness@solfut.com</a>.
                            </p>
                            <div className="flex gap-3 mt-6 justify-end">
                                <button onClick={closeOrder} className="btn-primary" data-testid="order-modal-close">Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function BankRow({ label, value, testid, mono, highlight, onCopy, copied }) {
    return (
        <div className="flex items-center justify-between gap-4 py-2 border-b border-[#e4e4e7] last:border-0" data-testid={testid}>
            <span className="label-caps text-xs">{label}</span>
            <div className="flex items-center gap-3">
                <span className={`text-right ${mono ? "font-mono" : ""} ${highlight ? "font-display text-2xl text-[#0d1628]" : "text-[#0d1628]"}`}>{value}</span>
                {onCopy && (
                    <button onClick={onCopy} className="text-[#52525b] hover:text-[#0d1628]" title="Copiar" data-testid={`${testid}-copy`}>
                        {copied ? <CheckCircle2 size={14} className="text-[#16a34a]" /> : <Copy size={14} />}
                    </button>
                )}
            </div>
        </div>
    );
}
